import React, { useState, useEffect, useCallback } from 'react';
import { CaretLeft, CaretRight, Calendar } from 'phosphor-react';
import Header from '../components/Header';
import { Tabs, TabsList, TabsTrigger } from '../components/Tabs';
import SearchField from '../components/SearchField';
import ProductTile from '../components/ProductTile';
import { PageNavBar } from '../components/PageNavBar';
import { getDailySummary, OrderSummaryRow } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';

interface ProductTotal {
  productId: number;
  productName: string;
  unidadDeNegocio: string;
  quantity: number;
}

interface CustomerProduct {
  productId: number;
  productName: string;
  unidadDeNegocio: string;
  quantity: number;
  orderDate: string;
  orderTime: string;
}

interface CustomerTotal {
  customerName: string;
  customerRut: string | null;
  products: CustomerProduct[];
}

interface DailyReportProps {
  onBack?: () => void;
  onMenuClick?: () => void;
}

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function deriveProductTotals(rows: OrderSummaryRow[]): ProductTotal[] {
  const map = new Map<number, ProductTotal>();
  for (const row of rows) {
    const key = row.product_id;
    const qty = Number(row.quantity) || 0;
    const existing = map.get(key);
    if (existing) {
      existing.quantity += qty;
    } else {
      map.set(key, {
        productId: row.product_id,
        productName: row.product_name,
        unidadDeNegocio: row.unidad_de_negocio,
        quantity: qty,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.productName.localeCompare(b.productName));
}

function deriveCustomerTotals(rows: OrderSummaryRow[]): CustomerTotal[] {
  const map = new Map<string, CustomerTotal>();
  for (const row of rows) {
    const key = row.customer_rut ?? row.customer_name ?? 'Sin cliente';
    const customerName = row.customer_name ?? 'Sin cliente';
    if (!map.has(key)) {
      map.set(key, { customerName, customerRut: row.customer_rut, products: [] });
    }
    const customer = map.get(key)!;
    const qty = Number(row.quantity) || 0;
    const existing = customer.products.find(p => p.productId === row.product_id);
    if (existing) {
      existing.quantity += qty;
    } else {
      customer.products.push({
        productId: row.product_id,
        productName: row.product_name,
        unidadDeNegocio: row.unidad_de_negocio,
        quantity: qty,
        orderDate: row.order_date,
        orderTime: row.order_time,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.customerName.localeCompare(b.customerName));
}

export const DailyReport: React.FC<DailyReportProps> = ({ onBack, onMenuClick }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedView, setSelectedView] = useState<'product' | 'customer'>('product');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [rows, setRows] = useState<OrderSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async (date: Date) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await getDailySummary(toLocalDateString(date), token);
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchSummary(selectedDate);
  }, [selectedDate, fetchSummary]);

  const productTotals = deriveProductTotals(rows);
  const customerTotals = deriveCustomerTotals(rows);

  const formatDate = (date: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) return 'Hoy';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.getTime() === yesterday.getTime()) return 'Ayer';
    return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const changeDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d);
  };

  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return today.getTime() === d.getTime();
  };

  const searchLower = searchValue.toLowerCase();

  const filteredProductTotals = productTotals.filter(p =>
    p.productName.toLowerCase().includes(searchLower)
  );

  const filteredCustomerTotals = customerTotals
    .map(c => ({
      ...c,
      products: c.products.filter(p => p.productName.toLowerCase().includes(searchLower)),
    }))
    .filter(c => c.customerName.toLowerCase().includes(searchLower) || c.products.length > 0);

  return (
    <div className="bg-muted flex flex-col relative w-full min-h-screen">
      <Header className="shrink-0 w-full" onMenuClick={onMenuClick} />

      <PageNavBar onBack={onBack} title="Reporte Diario" />

      <div className="flex flex-col items-center w-full">
        <div className="flex flex-col w-full max-w-[1280px] px-3">
          {/* Date Selector */}
          <div className="bg-muted flex flex-col gap-2 items-start pb-4 pt-4 shrink-0 w-full">
            <div className="flex items-center gap-2">
              <div
                className="flex items-center justify-center p-2 rounded-md cursor-pointer hover:bg-black/5 transition-colors duration-base ease-smooth"
                onClick={() => changeDate(-1)}
              >
                <CaretLeft size={20} weight="bold" className="text-foreground" />
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-black/5 transition-colors duration-base ease-smooth font-sans font-semibold text-xs text-foreground"
                >
                  <Calendar size={16} weight="bold" className="text-foreground" />
                  <span>{formatDate(selectedDate)}</span>
                </button>

                {showDatePicker && (
                  <div
                    className="absolute top-full left-0 mt-2 z-[15] bg-background border border-border rounded-md shadow-lg p-3"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="date"
                      value={toLocalDateString(selectedDate)}
                      onChange={e => {
                        if (e.target.value) {
                          setSelectedDate(new Date(e.target.value + 'T00:00:00'));
                          setShowDatePicker(false);
                        }
                      }}
                      className="font-sans text-sm text-foreground outline-none border border-border rounded px-2 py-1 bg-background"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              <div
                className="flex items-center justify-center p-2 rounded-md cursor-pointer hover:bg-black/5 transition-colors duration-base ease-smooth"
                onClick={() => changeDate(1)}
              >
                <CaretRight size={20} weight="bold" className="text-foreground" />
              </div>

              {!isToday() && (
                <button
                  type="button"
                  onClick={() => setSelectedDate(new Date())}
                  className="px-3 py-2 rounded-md hover:bg-black/5 transition-colors duration-base ease-smooth font-sans font-semibold text-xs text-foreground"
                >
                  Hoy
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="bg-muted flex flex-col gap-2 items-start pb-4 pt-2 shrink-0 sticky top-[56px] z-10 w-full">
            <div className="w-full max-w-[400px]">
              <SearchField
                placeholder="Buscar productos o clientes"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onClear={() => setSearchValue('')}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-muted flex flex-col gap-2 items-start py-2 shrink-0 sticky top-[104px] z-10 w-full">
            <div className="relative shrink-0 w-full overflow-x-auto">
              <Tabs value={selectedView} onValueChange={v => setSelectedView(v as 'product' | 'customer')}>
                <TabsList>
                  <TabsTrigger value="product">Totales por producto</TabsTrigger>
                  <TabsTrigger value="customer">Totales por cliente</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex py-2 relative w-full min-h-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <p className="font-sans text-sm text-muted-foreground">Cargando...</p>
              </div>
            ) : selectedView === 'product' ? (
              <div className="flex-1 bg-background flex flex-col items-start p-2 rounded-lg w-full overflow-y-auto">
                <div className="w-full">
                  {filteredProductTotals.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <p className="font-sans font-medium text-base text-muted-foreground text-center">
                        No hay pedidos para este día
                      </p>
                    </div>
                  ) : (
                    <div className="w-full">
                      <div className="hidden md:flex items-center px-0 py-2 pb-3 border-b border-border w-full" style={{ gap: '16px' }}>
                        <div className="w-[300px]">
                          <p className="font-sans font-semibold text-xs text-muted-foreground uppercase tracking-wide">Producto</p>
                        </div>
                        <div className="flex-1 flex justify-end">
                          <p className="font-sans font-semibold text-xs text-muted-foreground uppercase tracking-wide">Cantidad</p>
                        </div>
                      </div>
                      {filteredProductTotals.map(product => (
                        <ProductTile
                          key={product.productId}
                          name={product.productName}
                          sku={product.unidadDeNegocio}
                          quantity={product.quantity}
                          unit="unidades"
                          variant="report"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col gap-8 w-full overflow-y-auto">
                {filteredCustomerTotals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 bg-background rounded-lg">
                    <p className="font-sans font-medium text-base text-muted-foreground text-center">
                      No hay pedidos para este día
                    </p>
                  </div>
                ) : (
                  filteredCustomerTotals.map(customer => (
                    <div key={customer.customerRut ?? customer.customerName} className="flex flex-col gap-2 w-full">
                      <p className="font-sans font-semibold text-lg text-foreground px-2">
                        {customer.customerName}
                      </p>
                      <div className="bg-background flex flex-col items-start p-2 rounded-lg w-full">
                        <div className="w-full">
                          {customer.products.map(product => (
                            <ProductTile
                              key={product.productId}
                              name={product.productName}
                              sku={product.unidadDeNegocio}
                              quantity={product.quantity}
                              unit="unidades"
                              variant="report"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showDatePicker && (
        <div className="fixed inset-0 z-[5]" onClick={() => setShowDatePicker(false)} />
      )}
    </div>
  );
};

export default DailyReport;
